import React, { useEffect, useState } from "react";
import { X, Save, Trash2, RefreshCcw, Loader2, Bookmark } from "lucide-react";
import {
  listSavedStrategies,
  saveStrategy,
  deleteSavedStrategy,
} from "@/lib/optimizerApi";
import { toast } from "sonner";

const fmt$ = (n) =>
  n == null ? "—" :
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Right-side drawer listing the user's saved strategies with live P/L since
 * the day they hit Save. Each row shows entry mids vs current marks, plus
 * the high-water and low-water marks the backend tracks across calls.
 *
 * Built as a sidebar so users can keep the builder visible while reviewing
 * — closer to OptionStrat's "trade tracker" panel than a separate page.
 */
const SavedStrategiesDrawer = ({ open, onClose, strategy, onLoad }) => {
  const [saved, setSaved] = useState({ strategies: [], total_pnl: 0 });
  const [loading, setLoading] = useState(false);
  const [savingNow, setSavingNow] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const s = await listSavedStrategies();
      setSaved(s);
    } catch (err) {
      // Silent for guests (no auth header) — but log in dev so we catch
      // unexpected failures during development.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("SavedStrategiesDrawer: refresh failed", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const handleSave = async () => {
    if (!strategy?.metrics?.legs?.length) {
      toast.error("Build a strategy first");
      return;
    }
    const name = window.prompt(
      "Name this trade idea:",
      `${strategy?.template?.name || "Custom"} · ${new Date().toLocaleDateString()}`,
    );
    if (!name) return;
    setSavingNow(true);
    try {
      await saveStrategy({
        name,
        symbol: strategy?.pricing?.symbol || strategy?.inputs?.symbol || "",
        template_id: strategy?.template?.id,
        template_name: strategy?.template?.name,
        spot_at_save: strategy?.metrics?.spot,
        iv_at_save: strategy?.metrics?.iv,
        entry_net_premium: strategy?.metrics?.net_premium,
        legs: (strategy?.metrics?.legs || []).map((l) => ({
          action: l.action, type: l.type, strike: l.strike, dte: l.dte, qty: l.qty,
        })),
        entry_legs: (strategy?.metrics?.legs || []).map((l) => ({
          action: l.action, type: l.type, strike: l.strike, dte: l.dte, qty: l.qty,
          entry_mid: l.mid,
        })),
      });
      toast.success(`Saved: ${name}`);
      refresh();
    } catch {
      toast.error("Save failed (auth required?)");
    } finally {
      setSavingNow(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this saved trade?")) return;
    try {
      await deleteSavedStrategy(id);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-[60] w-full sm:w-[420px] bg-[#0a0a0c] border-l border-white/10 shadow-2xl flex flex-col"
      data-testid="saved-strategies-drawer"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#A98CFF] flex items-center gap-1.5">
            <Bookmark className="w-3 h-3" /> Saved trades
          </div>
          <div className="text-[13px] font-medium mt-0.5">
            {saved.count || 0} idea{(saved.count || 0) === 1 ? "" : "s"} ·{" "}
            <span
              className="num"
              style={{ color: (saved.total_pnl || 0) >= 0 ? "#00C805" : "#FF3B30" }}
            >
              {fmt$(saved.total_pnl)}
            </span>{" "}
            net
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            data-testid="saved-refresh"
            className="p-1 text-white/55 hover:text-white border border-white/15 rounded"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="saved-close"
            className="p-1 text-white/55 hover:text-white border border-white/15 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-white/10">
        <button
          type="button"
          onClick={handleSave}
          disabled={savingNow || !strategy?.metrics?.legs?.length}
          data-testid="save-current-strategy"
          className="w-full inline-flex items-center justify-center gap-1.5 bg-[#A855F7] text-white hover:bg-[#9333EA] px-4 py-2 rounded-md text-[12px] font-medium disabled:opacity-40"
        >
          {savingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save current build
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
        {!saved.strategies?.length && (
          <div className="text-center text-white/40 text-[12px] py-8 italic">
            No saved trades yet. Build something good and hit save.
          </div>
        )}
        {saved.strategies?.map((s) => {
          const positive = (s.last_pnl || 0) >= 0;
          return (
            <div
              key={s.id}
              className="border border-white/10 rounded-lg p-3 hover:border-white/25 transition"
              data-testid={`saved-row-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] font-medium text-white truncate">
                    {s.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5">
                    {s.symbol} · {s.template_name}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(s.id)}
                  className="text-white/30 hover:text-[#FF3B30] p-0.5"
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-white/40">Now</div>
                  <div
                    className="num font-medium"
                    style={{ color: positive ? "#00C805" : "#FF3B30" }}
                  >
                    {fmt$(s.last_pnl)}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-white/40">High</div>
                  <div className="num text-[#00C805]">{fmt$(s.pnl_high)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-white/40">Low</div>
                  <div className="num text-[#FF3B30]">{fmt$(s.pnl_low)}</div>
                </div>
              </div>
              {onLoad && (
                <button
                  type="button"
                  onClick={() => onLoad(s)}
                  className="mt-2 w-full text-[10.5px] uppercase tracking-[0.18em] py-1 rounded border border-[#A98CFF]/40 text-[#A98CFF] hover:bg-[#A98CFF]/10"
                >
                  Load into builder
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SavedStrategiesDrawer;
